import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  MdAdd,
  MdArrowForward,
  MdAssignmentTurnedIn,
  MdCheck,
  MdCheckCircle,
  MdChevronLeft,
  MdGroups,
  MdLink,
  MdUploadFile,
} from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import AlertPane from "components/alert";
import Button from "components/button";
import { InputField, Textarea } from "components/fields";
import Loader from "components/loader/Loader";
import { ErrorState } from "components/states";
import { useAuth } from "hooks/useAuth";
import notify from "lib/notify";
import pathwaysService from "services/pathwaysService";
import AddGuestModal from "../dashboard/AddGuestModal";
import CsvImportModal from "../dashboard/CsvImportModal";

// ─── Step Indicator ─────────────────────────────────────────────────────────
const STEPS = [
  { number: 1, label: "Connect" },
  { number: 2, label: "Survey" },
  { number: 3, label: "Launch" },
];

const StepIndicator = ({ currentStep }) => (
  <ol className="mb-10 flex items-center justify-center gap-3" aria-label="Setup progress">
    {STEPS.map((step, idx) => {
      const isActive = currentStep === step.number;
      const isCompleted = currentStep > step.number;

      return (
        <React.Fragment key={step.number}>
          {idx > 0 && (
            <li
              aria-hidden="true"
              className={`h-0.5 w-12 rounded-full transition-colors duration-300 ${
                isCompleted || isActive ? "bg-primary dark:bg-accent" : "bg-line"
              }`}
            />
          )}
          <li className="flex items-center gap-2" aria-current={isActive ? "step" : undefined}>
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors duration-300 ${
                isActive
                  ? "bg-primary text-primary-fg"
                  : isCompleted
                  ? "bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200"
                  : "bg-surface-subtle text-fg-muted"
              }`}
            >
              {isCompleted ? <MdCheck aria-hidden="true" className="h-4 w-4" /> : step.number}
              {isCompleted && <span className="sr-only">Completed:</span>}
            </span>
            <span
              className={`hidden text-sm font-medium transition-colors duration-300 sm:inline ${
                isActive ? "text-fg" : "text-fg-muted"
              }`}
            >
              {step.label}
            </span>
          </li>
        </React.Fragment>
      );
    })}
  </ol>
);

// Planning Center brand mark (third-party logo colors).
const PlanningCenterLogo = () => (
  <svg width="40" height="40" viewBox="0 0 28 30" fill="none" aria-hidden="true">
    <g clipPath="url(#pco-clip)">
      <path
        d="M24.7852 3.27941C26.7556 3.79412 28 5.64706 28 7.60294V21.2941C28 23.25 26.6519 25 24.7852 25.6176L16.2815 28.1912C14.8296 28.6029 13.1704 28.6029 11.7185 28.1912L3.21481 25.7206C1.34815 25.1029 0 23.25 0 21.2941V7.60294C0 5.64706 1.34815 3.89706 3.21481 3.27941L11.7185 0.808824C13.1704 0.397059 14.8296 0.397059 16.2815 0.808824L24.7852 3.27941Z"
        fill="#2565F4"
      />
      <path
        d="M18.5 9.3L13.7 10.7C13.5 10.7 13.4 10.8 13.2 10.7L8.4 9.3C7.4 9 6.5 9.7 6.5 10.7V21.5C6.5 21.7 6.6 21.9 6.8 21.9L8.6 22.4C8.9 22.5 9.1 22.3 9.1 22V19.5L9.2 19.4L12.8 20.5C13.3 20.6 13.8 20.6 14.2 20.5L19.6 18.8C20.2 18.7 20.5 18.3 20.5 17.8V10.8C20.5 9.8 19.5 9 18.5 9.3ZM17.9 16.5C17.9 16.6 17.8 16.7 17.7 16.7L13.8 17.9C13.6 18 13.4 18 13.2 17.9L9.4 16.8C9.3 16.8 9.2 16.7 9.2 16.6V12.6C9.2 12.5 9.3 12.4 9.5 12.4L12.6 13.3C13.2 13.5 13.9 13.5 14.5 13.3L17.6 12.4C17.7 12.4 17.9 12.5 17.9 12.6V16.5Z"
        fill="white"
      />
    </g>
    <defs>
      <clipPath id="pco-clip">
        <rect width="28" height="29" fill="white" transform="translate(0 0.5)" />
      </clipPath>
    </defs>
  </svg>
);

// ─── Step 1: Connect Planning Center ────────────────────────────────────────
const StepConnect = ({ onNext, syncedCount, setSyncedCount, setIsManual }) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("idle"); // idle | connecting | syncing | success | error
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [manualLoading, setManualLoading] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualGuestCount, setManualGuestCount] = useState(0);
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const hasHandledRedirect = useRef(false);

  // Handle OAuth redirect or check if already connected
  useEffect(() => {
    if (hasHandledRedirect.current) return;

    const pcConnected = searchParams.get("pc_connected");
    const pcError = searchParams.get("pc_error");

    if (pcConnected === "true") {
      hasHandledRedirect.current = true;
      handleSync();
    } else if (pcError === "true") {
      hasHandledRedirect.current = true;
      setStatus("error");
      setErrorMessage("Could not connect to Planning Center. Try again.");
    } else {
      // Check if PCO is already connected for THIS org (resolved via church slug,
      // not the logged-in user's org — matters for super_admins onboarding a church).
      hasHandledRedirect.current = true;
      pathwaysService
        .getConnectionStatus()
        .then((res) => {
          if (res?.data?.isAccessTokenSet) {
            handleSync();
          }
        })
        .catch(() => {}); // Not connected — show the connect button
    }
  }, [searchParams]);

  const handleSync = async () => {
    try {
      setStatus("syncing");
      setStatusMessage("Connecting to Planning Center…");

      await new Promise((r) => setTimeout(r, 800));
      setStatusMessage("Syncing your recent guests…");

      const response = await pathwaysService.syncPco();
      const count = response?.data?.data?.syncedCount ?? response?.data?.syncedCount ?? 0;

      setStatusMessage("Setting up your account…");
      await new Promise((r) => setTimeout(r, 600));

      setSyncedCount(count);
      setStatus("success");
    } catch (err) {
      console.error("PCO sync failed:", err);
      setStatus("error");
      setErrorMessage("Could not sync with Planning Center. Try again.");
    }
  };

  const handleConnect = async () => {
    setStatus("connecting");
    try {
      const response = await AtriumApi.get("/v1/planning-center/oauth-url");
      if (response?.data?.authUrl) {
        window.location.href = response.data.authUrl;
      } else {
        setStatus("error");
        setErrorMessage("Planning Center integration is not configured. Contact support.");
      }
    } catch (err) {
      console.error("Failed to get OAuth URL:", err);
      setStatus("error");
      setErrorMessage("Could not start the Planning Center connection. Try again.");
    }
  };

  const handleRetry = () => {
    setStatus("idle");
    setErrorMessage("");
  };

  const handleManualSetup = async () => {
    setManualLoading(true);
    try {
      await pathwaysService.setSetupMode("manual");
      // Stay on this step and reveal the inline guest-import panel rather than
      // jumping straight to Survey — manual churches load their people here.
      setManualMode(true);
      setIsManual?.(true);
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not switch to manual setup"));
    } finally {
      setManualLoading(false);
    }
  };

  return (
    <div className="text-center">
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-card bg-surface-subtle">
        <PlanningCenterLogo />
      </div>

      <h1 className="mb-3 text-title">
        {manualMode ? "Add Your First-Time Guests" : "Connect Your Planning Center Account"}
      </h1>
      <p className="mx-auto mb-8 max-w-md text-fg-muted">
        {manualMode
          ? "Upload a CSV of your recent guests (we'll smart-match the columns) or add them one at a time. You can also do this later from your dashboard."
          : "We'll sync your recent guest data from Planning Center so you can start engaging first-time visitors right away."}
      </p>

      {/* Idle State: PCO connect / manual fork */}
      {status === "idle" && !manualMode && (
        <div className="flex flex-col items-center gap-3">
          <Button size="lg" onClick={handleConnect}>
            Connect to Planning Center
          </Button>
          <Button variant="ghost" onClick={handleManualSetup} loading={manualLoading}>
            {manualLoading ? "Setting Up…" : "Set Up Without Planning Center"}
          </Button>
        </div>
      )}

      {/* Manual mode: inline guest import panel */}
      {status === "idle" && manualMode && (
        <div className="flex flex-col items-center gap-5">
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <Button
              size="lg"
              leftIcon={<MdUploadFile aria-hidden="true" />}
              onClick={() => setShowImport(true)}
            >
              Import CSV
            </Button>
            <Button
              variant="secondary"
              size="lg"
              leftIcon={<MdAdd aria-hidden="true" />}
              onClick={() => setShowAddGuest(true)}
            >
              Add Guest
            </Button>
          </div>

          {manualGuestCount > 0 && (
            <p className="text-sm font-medium text-fg" role="status">
              {manualGuestCount} guest{manualGuestCount === 1 ? "" : "s"} added
            </p>
          )}

          <div className="mt-2 flex items-center gap-3">
            <Button variant="ghost" onClick={onNext}>
              {manualGuestCount > 0 ? "Skip for Now" : "Add Guests Later"}
            </Button>
            {manualGuestCount > 0 && (
              <Button onClick={onNext} rightIcon={<MdArrowForward aria-hidden="true" />}>
                Continue
              </Button>
            )}
          </div>
        </div>
      )}

      <AddGuestModal
        open={showAddGuest}
        onClose={() => setShowAddGuest(false)}
        onCreated={() => {
          setManualGuestCount((c) => c + 1);
          setSyncedCount((c) => c + 1);
        }}
      />
      <CsvImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={(result) => {
          const n = result?.created || 0;
          setManualGuestCount((c) => c + n);
          setSyncedCount((c) => c + n);
        }}
        onComplete={(result) => {
          // After a successful import, advance to the Survey step so the user
          // gets clear forward progress instead of landing back on the import screen.
          const imported = (result?.created || 0) + (result?.updated || 0);
          if (imported > 0) onNext();
        }}
      />

      {status === "connecting" && (
        <div className="flex items-center justify-center gap-3 text-fg-muted">
          <Loader size="sm" variant="inline" label="Redirecting" />
          <span>Redirecting to Planning Center…</span>
        </div>
      )}

      {status === "syncing" && (
        <div className="flex flex-col items-center gap-4">
          <Loader size="lg" variant="inline" label={statusMessage} />
          <p className="font-medium text-fg-muted" aria-hidden="true">
            {statusMessage}
          </p>
        </div>
      )}

      {status === "success" && (
        <div className="space-y-6">
          <div className="inline-flex items-center gap-3 rounded-card bg-surface-subtle px-6 py-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-fg">
              <MdCheck aria-hidden="true" className="h-5 w-5" />
            </span>
            <div className="text-left">
              <p className="font-semibold text-fg">Planning Center Connected</p>
              <p className="text-sm text-fg-muted">
                Found <span className="font-bold text-fg">{syncedCount}</span> recent guests
              </p>
            </div>
          </div>

          <div>
            <Button size="lg" onClick={onNext} rightIcon={<MdArrowForward aria-hidden="true" />}>
              Next
            </Button>
          </div>
        </div>
      )}

      {status === "error" && (
        <div className="mx-auto max-w-md space-y-4">
          <AlertPane tone="danger" className="text-left">
            {errorMessage}
          </AlertPane>
          <Button onClick={handleRetry}>Try Again</Button>
        </div>
      )}
    </div>
  );
};

// ─── Step 2: Review Survey Questions ────────────────────────────────────────
const StepSurvey = ({ onNext, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [originalQuestions, setOriginalQuestions] = useState([]);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await pathwaysService.getSurveyQuestions();
      const initial = response?.data?.data?.initial || response?.data?.initial || [];
      setQuestions(JSON.parse(JSON.stringify(initial)));
      setOriginalQuestions(JSON.parse(JSON.stringify(initial)));
    } catch (err) {
      setLoadError(err);
    } finally {
      setLoading(false);
    }
  };

  const updateQuestionText = (index, newText) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], text: newText };
      return updated;
    });
  };

  const updateAnswerChoice = (qIndex, aIndex, newText) => {
    setQuestions((prev) => {
      const updated = [...prev];
      const choices = [...(updated[qIndex].options || [])];
      choices[aIndex] = newText;
      updated[qIndex] = { ...updated[qIndex], options: choices };
      return updated;
    });
  };

  const resetQuestion = (index) => {
    setQuestions((prev) => {
      const updated = [...prev];
      updated[index] = JSON.parse(JSON.stringify(originalQuestions[index]));
      return updated;
    });
  };

  const handleApprove = async () => {
    try {
      setSaving(true);
      await pathwaysService.approveSurvey(questions);
      onNext();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not save survey"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16">
        <Loader size="lg" variant="inline" label="Loading survey questions" />
        <p className="text-fg-muted" aria-hidden="true">
          Loading survey questions…
        </p>
      </div>
    );
  }

  if (loadError && questions.length === 0) {
    return (
      <ErrorState
        message={getApiErrorMessage(loadError, "Could not load survey questions.")}
        onRetry={fetchQuestions}
      />
    );
  }

  return (
    <div>
      <div className="mb-6 text-center">
        <h1 className="mb-2 text-title">Review Your Guest Survey</h1>
        <p className="text-fg-muted">These questions will be sent to your first-time guests</p>
      </div>

      <AlertPane tone="warning" className="mb-6">
        Editing the content of these questions may affect the quality of insights. We recommend
        keeping the intent intact.
      </AlertPane>

      <ol className="mb-8 divide-y divide-line">
        {questions.map((q, qIndex) => (
          <li key={qIndex} className="space-y-3 py-5 first:pt-0">
            <div className="flex items-center justify-between">
              <span
                id={`question-${qIndex}-label`}
                className="text-xs font-semibold uppercase tracking-wide text-fg-muted"
              >
                Question {qIndex + 1}
              </span>
              <Button variant="ghost" size="sm" onClick={() => resetQuestion(qIndex)}>
                Reset to Default
              </Button>
            </div>

            <Textarea
              aria-labelledby={`question-${qIndex}-label`}
              value={q.text || ""}
              onChange={(e) => updateQuestionText(qIndex, e.target.value)}
              rows={2}
            />

            {q.options && q.options.length > 0 && (
              <fieldset className="space-y-2">
                <legend className="mb-2 text-xs font-medium text-fg-muted">Answer Choices</legend>
                {q.options.map((choice, aIndex) => (
                  <InputField
                    key={aIndex}
                    aria-label={`Question ${qIndex + 1} answer choice ${aIndex + 1}`}
                    value={choice || ""}
                    onChange={(e) => updateAnswerChoice(qIndex, aIndex, e.target.value)}
                  />
                ))}
              </fieldset>
            )}
          </li>
        ))}
      </ol>

      <div className="flex items-center justify-between">
        <Button variant="ghost" leftIcon={<MdChevronLeft aria-hidden="true" />} onClick={onBack}>
          Back
        </Button>
        <Button size="lg" onClick={handleApprove} loading={saving}>
          {saving ? "Saving…" : "Approve & Continue"}
        </Button>
      </div>
    </div>
  );
};

// ─── Step 3: Get Started ────────────────────────────────────────────────────
const StepLaunch = ({ syncedCount, isManual, onBack }) => {
  const navigate = useNavigate();
  const { setUser, user } = useAuth();
  const [completing, setCompleting] = useState(false);

  const handleLaunch = async () => {
    try {
      setCompleting(true);
      await pathwaysService.completeOnboarding();

      // Update user state so the guard doesn't redirect back
      if (user) {
        setUser({ ...user, pathwaysOnboardingCompleted: true });
      }

      navigate("/admin/pathways/dashboard");
    } catch (err) {
      console.error("Failed to complete onboarding:", err);
      // Navigate anyway — the backend state may have already updated
      navigate("/admin/pathways/dashboard");
    }
  };

  const guestNoun = syncedCount === 1 ? "Guest" : "Guests";
  const summaryItems = [
    isManual
      ? { label: "Manual Setup Complete", detail: "No Planning Center needed", icon: MdCheck }
      : { label: "Planning Center Connected", detail: "Synced and ready", icon: MdLink },
    {
      label: isManual
        ? `${syncedCount} ${guestNoun} Added`
        : `${syncedCount} Recent ${guestNoun} Found`,
      detail: "Ready for outreach",
      icon: MdGroups,
    },
    {
      label: "Survey Ready to Send",
      detail: "Personalized for your church",
      icon: MdAssignmentTurnedIn,
    },
  ];

  return (
    <div className="text-center">
      <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary text-primary-fg">
        <MdCheck aria-hidden="true" className="h-10 w-10" />
      </div>

      <h1 className="mb-2 text-title">You're All Set</h1>
      <p className="mb-8 text-fg-muted">Your Pathways account is configured and ready to go.</p>

      <ul className="mx-auto mb-10 max-w-sm space-y-3">
        {summaryItems.map((item) => (
          <li
            key={item.label}
            className="flex items-center gap-4 rounded-control bg-surface-subtle p-4 text-left"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-fg">
              <item.icon aria-hidden="true" className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-fg">{item.label}</p>
              <p className="text-xs text-fg-muted">{item.detail}</p>
            </div>
            <MdCheckCircle aria-hidden="true" className="ml-auto h-5 w-5 text-primary dark:text-accent" />
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between">
        <Button variant="ghost" leftIcon={<MdChevronLeft aria-hidden="true" />} onClick={onBack}>
          Back
        </Button>
        <Button size="lg" onClick={handleLaunch} loading={completing}>
          {completing ? "Finishing…" : "Go to Dashboard"}
        </Button>
      </div>
    </div>
  );
};

// ─── Main Wizard ────────────────────────────────────────────────────────────
const PathwaysOnboarding = () => {
  const [searchParams] = useSearchParams();
  const startStep = searchParams.get("step") === "survey" ? 2 : 1;
  const [currentStep, setCurrentStep] = useState(startStep);
  const [syncedCount, setSyncedCount] = useState(0);
  const [isManual, setIsManual] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-canvas text-fg">
      <header className="w-full px-6 py-6">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <span
            aria-hidden="true"
            className="flex h-8 w-8 items-center justify-center rounded-control bg-primary text-sm font-bold text-primary-fg"
          >
            A
          </span>
          <span className="text-lg font-bold text-fg">Atrium Pathways</span>
        </div>
      </header>

      <main className="flex flex-1 items-start justify-center px-6 pb-12 pt-4">
        <div className="w-full max-w-2xl">
          <StepIndicator currentStep={currentStep} />

          <div className="rounded-card border border-line bg-surface p-6 shadow-card dark:shadow-none sm:p-10">
            {currentStep === 1 && (
              <StepConnect
                onNext={() => setCurrentStep(2)}
                syncedCount={syncedCount}
                setSyncedCount={setSyncedCount}
                setIsManual={setIsManual}
              />
            )}
            {currentStep === 2 && (
              <StepSurvey
                onNext={() => setCurrentStep(3)}
                onBack={() => setCurrentStep(1)}
              />
            )}
            {currentStep === 3 && (
              <StepLaunch
                syncedCount={syncedCount}
                isManual={isManual}
                onBack={() => setCurrentStep(2)}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PathwaysOnboarding;
